import axios from "axios";

/**
 * The three Simbiosis endpoints this app uses, keyed by the 100ms room id.
 * Their shapes are a contract with ../simbiosis; see
 * SIMBIOSIS-LIVE-INTERVIEWS.md section 4 before changing anything here.
 */
function apiUrl() {
  return process.env.REACT_APP_API;
}

const RETRYABLE_STATUSES = [429, 502, 503, 504];
const MAX_ATTEMPTS = 3;
const MAX_DELAY_MS = 10000;

function retryDelay(error, attempt) {
  const retryAfter = Number(error.response?.headers?.["retry-after"]);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, MAX_DELAY_MS);
  }
  return Math.min(1000 * 2 ** attempt, MAX_DELAY_MS);
}

/**
 * GETs that survive the platform's rate limit (60 requests a minute per IP)
 * and brief outages: 429 and gateway errors are retried with backoff,
 * honouring Retry-After. Anything else (a 404 for an unknown room) fails at
 * once.
 */
async function getWithRetry(url, { sleep = defaultSleep } = {}) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if (!RETRYABLE_STATUSES.includes(status) || attempt + 1 >= MAX_ATTEMPTS) {
        throw error;
      }
      await sleep(retryDelay(error, attempt));
    }
  }
}

function defaultSleep(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

/** `{ partner_interview, project, profile }` */
export function fetchInterviewInfo(roomId, options) {
  return getWithRetry(
    `${apiUrl()}/interview-info/${encodeURIComponent(roomId)}`,
    options
  );
}

/** `{ recording_enabled }` */
export function fetchInterviewRecording(roomId, options) {
  return getWithRetry(
    `${apiUrl()}/interview-recording/${encodeURIComponent(roomId)}`,
    options
  );
}

/** `feedback` is `{ rating, comment, respondent }`; resolves to the feedback uuid. */
export function sendInterviewFeedback(roomId, feedback) {
  return axios
    .post(
      `${apiUrl()}/interview-feedback/${encodeURIComponent(roomId)}`,
      feedback
    )
    .then(response => response.data);
}
